import React, { useState, useEffect } from 'react';
import { Cloud, Sun, CloudRain, Wind, Thermometer, Droplets, Eye, Gauge, Sunrise, Sunset } from 'lucide-react';
import axios from 'axios';
import Header from '../Components/Header';

const Forecast = () => {
  const [forecastData, setForecastData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Simulating API call with sample data
    const fetchWeatherForecast = async () => {
      try {
        const response = await axios.get('/api/weather-forecast');
        setForecastData(response.data);
      } catch (err) {
        console.error('Error fetching weather forecast:', err);
        setError(err.message);
      }
    };

    fetchWeatherForecast();
  }, []);

  const getWeatherIcon = (condition, size = 'w-8 h-8') => {
    const iconClass = `${size} text-blue-500`;
    switch (condition?.toLowerCase()) {
      case 'clear':
      case 'clear-day':
        return <Sun className={`${iconClass} text-yellow-500`} />;
      case 'partly-cloudy-day':
      case 'partly cloudy':
        return <Cloud className={`${iconClass} text-gray-500`} />;
      case 'cloudy':
      case 'overcast':
        return <Cloud className={`${iconClass} text-gray-600`} />;
      case 'rain':
        return <CloudRain className={`${iconClass} text-blue-600`} />;
      default:
        return <Sun className={`${iconClass} text-yellow-500`} />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const convertFahrenheit = (temp) => {
    return Math.round((temp - 32) * 5/9);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Weather</h2>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!forecastData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Loading Weather</h2>
            <p className="text-gray-600">Fetching the latest forecast...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <Header/>
    
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Weather Forecast</h1>
          <p className="text-gray-600 text-lg">
            {forecastData.resolvedAddress} • {forecastData.timezone}
          </p>
        </div>

        {/* Daily Forecast Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {forecastData.days.map((day, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
              {/* Card Header */}
              <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-semibold">{formatDate(day.datetime)}</h3>
                    <p className="text-blue-100 capitalize">{day.conditions}</p>
                  </div>
                  {getWeatherIcon(day.icon, 'w-12 h-12')}
                </div>
              </div>

              {/* Temperature Section */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-800">{convertFahrenheit(day.temp)}°C</div>
                    <div className="text-sm text-gray-500">Current</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-red-500">{convertFahrenheit(day.tempmax)}°</div>
                    <div className="text-sm text-gray-500">High</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-blue-500">{convertFahrenheit(day.tempmin)}°</div>
                    <div className="text-sm text-gray-500">Low</div>
                  </div>
                </div>

                {/* Weather Details Grid */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center space-x-2">
                    <Droplets className="w-4 h-4 text-blue-500" />
                    <div>
                      <div className="text-sm font-medium">{day.humidity}%</div>
                      <div className="text-xs text-gray-500">Humidity</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Wind className="w-4 h-4 text-gray-500" />
                    <div>
                      <div className="text-sm font-medium">{day.windspeed} mph</div>
                      <div className="text-xs text-gray-500">Wind</div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Eye className="w-4 h-4 text-green-500" />
                    <div>
                      <div className="text-sm font-medium">{day.visibility} mi</div>
                      <div className="text-xs text-gray-500">Visibility</div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Gauge className="w-4 h-4 text-purple-500" />
                    <div>
                      <div className="text-sm font-medium">{day.pressure} mb</div>
                      <div className="text-xs text-gray-500">Pressure</div>
                    </div>
                  </div>
                </div>

                {/* Sun Times */}
                <div className="border-t pt-4 mt-4">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-2">
                      <Sunrise className="w-4 h-4 text-orange-500" />
                      <div>
                        <div className="text-sm font-medium">{day.sunrise}</div>
                        <div className="text-xs text-gray-500">Sunrise</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Sunset className="w-4 h-4 text-orange-600" />
                      <div>
                        <div className="text-sm font-medium">{day.sunset}</div>
                        <div className="text-xs text-gray-500">Sunset</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700 text-center">{day.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Info Card */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Location Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{forecastData.latitude.toFixed(4)}</div>
              <div className="text-sm text-gray-600">Latitude</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{forecastData.longitude.toFixed(4)}</div>
              <div className="text-sm text-gray-600">Longitude</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">UTC+{forecastData.tzoffset}</div>
              <div className="text-sm text-gray-600">Timezone Offset</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Forecast;